import React, { lazy, Suspense, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { Box, Grid, IconButton } from '@mui/material'
import { GridToolbarContainer } from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import QuotationAPI from 'api/orbitV1/QuotationAPI'
import { Search } from 'assets/icons/circles/Search'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import StatContainer from 'common/AbstractStatLine/StatContainer'
import PageLayout from 'common/Layout/Page/PageLayout'
import LayoutSkeleton from 'common/Skeletons/LayoutSkeleton'
import TableSkeleton from 'common/Skeletons/TableSkeleton'
import chips from 'common/ui/chips'
import { ColorBoxContent } from 'common/ui/colorBox'
import { getDate } from 'helpers/date'
import useOrbitFlag from 'hooks/useOrbitFlag'

import ViewQuotationModal from './components/ViewQuotationModal'

const CustomSearchbar = lazy(
  () => import('common/AbstractDataGrid/CustomSearchbar')
)

interface DataArrayProps {
  title: string
  info: number
}

interface QuotationProps {
  id: string
  quotationId: string
  orgName: string
  createdAt: Date
  expiryAt: Date
  status: string
  items: []
}

export default function QuotationManagement() {
  const [quotations, setQuotations] = useState<QuotationProps[] | []>([])
  const [quotationDetails, setQuotationDetails] = useState({})
  const [dataArray, setDataArray] = useState<DataArrayProps[] | []>([])
  const [isQuotationModalOpen, setIsQuotationModalOpen] = useState<
    boolean | undefined
  >(false)
  const FlagStatContainer = useOrbitFlag('statContainer')

  /* useQuery to retrieve data */
  const {
    status,
    data: quotationData,
    error: getQuotationsError
  } = useQuery(['getQuotations', 'quotationManagement'], async () => {
    const { data } = await QuotationAPI.getAllQuotations()
    return data
  })

  if (getQuotationsError) {
    toast.error('Error loading quotations. Please try again later')
  }

  useEffect(() => {
    if (status === 'success') {
      setQuotations(quotationData)
      setDataArray([
        { title: 'Total Quotations', info: quotationData.length },
        {
          title: 'Total Paid',
          info: quotationData.filter(
            (quotation) => quotation.status.toUpperCase() === 'PAID'
          ).length
        },
        {
          title: 'Total Pending',
          info: quotationData.filter(
            (quotation) => quotation.status.toUpperCase() === 'PENDING'
          ).length
        }
      ])
    }
  }, [quotationData])

  /* Data Grid Components */
  const rows = quotations.map((quotation) => {
    return {
      id: quotation._id,
      quotationId: quotation._id,
      orgName: quotation.orgName,
      createdAt: getDate(quotation.createdAt),
      expiryAt: getDate(quotation.expiryAt),
      status: quotation.status,
      items: quotation.items
    }
  })

  const columns = [
    { field: 'quotationId', headerName: 'Quotation ID', width: 150 },
    { field: 'orgName', headerName: 'Organization', width: 100 },
    { field: 'createdAt', headerName: 'Created At', width: 100 },
    { field: 'expiryAt', headerName: 'Expiry At', width: 100 },
    {
      field: 'status',
      headerName: 'Status',
      width: 50,
      renderCell: (params) => {
        const quotation = params.row
        return chips.quotes[quotation.status.toUpperCase()]
      }
    },
    {
      field: 'actions',
      headerName: 'Action',
      width: 50,
      renderCell: (params) => {
        // TODO: Implement viewing of quotation
        const quotation = params.row
        return (
          <>
            <IconButton
              sx={{ color: 'darkblue' }}
              onClick={() => {
                setQuotationDetails(quotation)
                setIsQuotationModalOpen(true)
              }}
              data-cy='actionBtn'
            >
              <Search />
            </IconButton>
          </>
        )
      }
    }
  ]

  const AccountToolbar = () => {
    return (
      <GridToolbarContainer
        sx={{
          justifyContent: 'space-between'
        }}
      >
        <CustomSearchbar />
      </GridToolbarContainer>
    )
  }

  return (
    <Suspense fallback={<LayoutSkeleton />}>
      <PageLayout header='Manage Quotation'>
        <Grid container direction='column' spacing={2}>
          <Grid item>
            {FlagStatContainer && <StatContainer dataArray={dataArray} />}
          </Grid>
          <Grid item>
            {status === 'loading' ? (
              <TableSkeleton />
            ) : (
              <ColorBoxContent>
                <Box>
                  <MUIDataGrid
                    initialState={{
                      sorting: {
                        sortModel: [{ field: 'expiryAt', sort: 'desc' }]
                      }
                    }}
                    rows={rows}
                    columns={columns}
                    components={{ Toolbar: AccountToolbar }}
                  />
                </Box>
              </ColorBoxContent>
            )}
          </Grid>
        </Grid>
      </PageLayout>
      {isQuotationModalOpen && (
        <ViewQuotationModal
          quotationDetails={quotationDetails}
          isQuotationModalOpen={isQuotationModalOpen}
          setIsQuotationModalOpen={setIsQuotationModalOpen}
        />
      )}
    </Suspense>
  )
}
